import React, { useState } from 'react';
import { IoLocationOutline, IoNavigateOutline } from 'react-icons/io5';
import { getCurrentLocation, isGeolocationSupported } from '../utils/geolocation';
import { useLanguage } from '../contexts/LanguageContext';
import './LocationButton.css';

const LocationButton = ({ 
  onLocationFound, 
  onLocationError, 
  className = '',
  variant = 'primary', // 'primary' or 'secondary'
  disabled = false 
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const handleLocationRequest = async () => {
    if (disabled || loading) return;

    setLoading(true);
    
    try {
      // For iOS Safari, we need to call getCurrentLocation immediately in the click handler
      // to maintain the user gesture context
      const location = await getCurrentLocation();
      
      if (onLocationFound) {
        onLocationFound(location.lat, location.lon, location.accuracy);
      }
    } catch (error) {
      console.error('Location error:', error);
      
      // Provide more helpful error messages for iOS users
      let errorMessage = error.message;
      if (error.message.includes('denied')) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
          errorMessage = 'Location access blocked. Try: 1) Clear Safari website data for this site, or 2) Go to Settings > Safari > Location Services and ensure it\'s enabled';
        }
      }
      
      if (onLocationError) {
        onLocationError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Don't render if geolocation is not supported
  if (!isGeolocationSupported()) {
    return null;
  }

  return (
    <button
      onClick={handleLocationRequest}
      disabled={disabled || loading}
      className={`location-button location-button--${variant} ${className} ${
        loading ? 'location-button--loading' : ''
      }`}
      aria-label={loading ? t('gettingLocation') : t('useCurrentLocation')}
    >
      <div className="location-button__content">
        {loading ? (
          <>
            <div className="location-button__spinner" />
            <span>{t('gettingLocation')}</span>
          </>
        ) : (
          <>
            <IoNavigateOutline size={20} className="location-button__icon" />
            <span>{t('useCurrentLocation')}</span>
          </>
        )}
      </div>
    </button>
  );
};

export default LocationButton;